import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, Check, Gauge, Compass, ListChecks, RefreshCw } from "lucide-react";
import { PageHero } from "@/components/common/PageHero";
import { api } from "@/lib/api";

const QUESTIONS = [
  {
    id: "q1",
    question: "What is your target timeline to launch operations in India?",
    options: [
      { label: "Within 30–60 days (speed is critical)", score: 3 },
      { label: "3–6 months (structured rollout)", score: 2 },
      { label: "6–12 months (exploratory phase)", score: 1 },
    ],
  },
  {
    id: "q2",
    question: "What is your target initial team size in India?",
    options: [
      { label: "50+ enterprise-scale hub", score: 3 },
      { label: "15–50 core team", score: 2 },
      { label: "5–15 engineers / specialists", score: 1 },
    ],
  },
  {
    id: "q3",
    question: "Which operating model best matches your long-term vision?",
    options: [
      { label: "Full GCC build & manage (dedicated center)", score: 3 },
      { label: "Build-Operate-Transfer (BOT)", score: 2 },
      { label: "Managed / fractional operations", score: 1 },
    ],
  },
  {
    id: "q4",
    question: "How clear is your India expansion plan today?",
    options: [
      { label: "Very clear — we know exactly what we want", score: 3 },
      { label: "Somewhat clear — we have a direction", score: 2 },
      { label: "Still exploring — we need guidance", score: 1 },
    ],
  },
];

export default function Assessment() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0); // 0..3 questions, 4 = details, 5 = result
  const [answers, setAnswers] = useState({});
  const [details, setDetails] = useState({ name: "", email: "", company: "" });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const totalSteps = QUESTIONS.length + 1;
  const progress = result ? 100 : Math.round((step / totalSteps) * 100);

  const pick = (q, opt) => {
    setAnswers((a) => ({ ...a, [q.id]: opt }));
    setTimeout(() => setStep((s) => s + 1), 220);
  };

  const submit = async () => {
    setLoading(true);
    try {
      const payload = {
        ...details,
        answers: QUESTIONS.map((q) => ({
          question: q.question,
          answer: answers[q.id]?.label || "",
          score: answers[q.id]?.score || 0,
        })),
      };
      const { data } = await api.post("/assessment", payload);
      setResult(data);
      setStep(5);
      toast.success("Your India Readiness Score is ready.");
    } catch (e) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const reset = () => { setStep(0); setAnswers({}); setResult(null); setDetails({ name: "", email: "", company: "" }); };

  return (
    <>
      <PageHero
        kicker="India Readiness Assessment™"
        title="How ready is your business for India?"
        subtitle="Answer four quick questions to get your readiness score, a suggested entry model, and recommended next steps."
      />

      <section className="section-pad" data-testid="assessment-widget">
        <div className="container-px max-w-3xl">
          {/* progress */}
          <div className="mb-10">
            <div className="mb-2 flex justify-between text-xs text-zinc-500">
              <span className="font-mono">{result ? "Complete" : `Step ${Math.min(step + 1, totalSteps)} of ${totalSteps}`}</span>
              <span className="font-mono">{progress}%</span>
            </div>
            <div className="h-1.5 overflow-hidden rounded-full bg-white/10">
              <motion.div className="h-full grad-accent" animate={{ width: `${progress}%` }} transition={{ duration: 0.5 }} />
            </div>
          </div>

          <AnimatePresence mode="wait">
            {/* Questions */}
            {step < QUESTIONS.length && (
              <motion.div key={`q-${step}`} initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -40 }} transition={{ duration: 0.4 }}>
                <h2 className="font-head text-2xl font-bold text-white sm:text-3xl" data-testid="assessment-question">
                  {QUESTIONS[step].question}
                </h2>
                <div className="mt-8 space-y-3">
                  {QUESTIONS[step].options.map((opt, i) => {
                    const selected = answers[QUESTIONS[step].id]?.label === opt.label;
                    return (
                      <button
                        key={i}
                        onClick={() => pick(QUESTIONS[step], opt)}
                        data-testid={`assessment-option-${step}-${i}`}
                        className={`flex w-full items-center justify-between rounded-2xl border p-5 text-left transition-all duration-200 ${
                          selected ? "border-orange-500/50 bg-orange-500/10" : "border-white/10 bg-white/[0.02] hover:border-orange-500/30 hover:bg-white/[0.04]"
                        }`}
                      >
                        <span className="text-sm font-medium text-zinc-200 sm:text-base">{opt.label}</span>
                        <span className={`flex h-6 w-6 items-center justify-center rounded-full border ${selected ? "grad-accent border-transparent text-[#1a0f00]" : "border-white/20"}`}>
                          {selected && <Check className="h-3.5 w-3.5" />}
                        </span>
                      </button>
                    );
                  })}
                </div>
                {step > 0 && (
                  <button onClick={() => setStep((s) => s - 1)} className="mt-8 inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white" data-testid="assessment-back">
                    <ArrowLeft className="h-4 w-4" /> Back
                  </button>
                )}
              </motion.div>
            )}

            {/* Details */}
            {step === QUESTIONS.length && (
              <motion.div key="details" initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -40 }} transition={{ duration: 0.4 }}>
                <h2 className="font-head text-2xl font-bold text-white sm:text-3xl">Where should we send your results?</h2>
                <p className="mt-3 text-sm text-zinc-400">Optional — add your details and we'll email a copy of your readiness summary.</p>
                <div className="mt-8 space-y-4">
                  <Field label="Name" value={details.name} onChange={(v) => setDetails({ ...details, name: v })} testid="assessment-name" />
                  <Field label="Work Email" type="email" value={details.email} onChange={(v) => setDetails({ ...details, email: v })} testid="assessment-email" />
                  <Field label="Company" value={details.company} onChange={(v) => setDetails({ ...details, company: v })} testid="assessment-company" />
                </div>
                <div className="mt-8 flex items-center gap-4">
                  <button onClick={() => setStep((s) => s - 1)} className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white" data-testid="assessment-details-back">
                    <ArrowLeft className="h-4 w-4" /> Back
                  </button>
                  <button onClick={submit} disabled={loading} className="btn-primary ml-auto disabled:opacity-60" data-testid="assessment-submit">
                    {loading ? "Calculating…" : "See My Readiness Score"} <ArrowRight className="h-4 w-4" />
                  </button>
                </div>
              </motion.div>
            )}

            {/* Result */}
            {result && step === 5 && (
              <motion.div key="result" initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5 }} data-testid="assessment-result">
                <div className="rounded-[2rem] border border-orange-500/20 bg-[#17171A] p-8 text-center sm:p-12">
                  <p className="kicker mb-6 flex justify-center gap-2">Your Result</p>
                  <div className="relative mx-auto flex h-40 w-40 items-center justify-center">
                    <svg className="absolute inset-0 -rotate-90" viewBox="0 0 100 100">
                      <circle cx="50" cy="50" r="44" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="8" />
                      <motion.circle cx="50" cy="50" r="44" fill="none" stroke="url(#g)" strokeWidth="8" strokeLinecap="round"
                        strokeDasharray={276} initial={{ strokeDashoffset: 276 }} animate={{ strokeDashoffset: 276 - (276 * result.score) / 100 }} transition={{ duration: 1.2, ease: "easeOut" }} />
                      <defs><linearGradient id="g" x1="0" y1="0" x2="1" y2="1"><stop stopColor="#FB923C" /><stop offset="1" stopColor="#F59E0B" /></linearGradient></defs>
                    </svg>
                    <div>
                      <div className="font-head text-4xl font-extrabold text-white">{result.score}%</div>
                      <div className="text-xs text-orange-400">{result.band}</div>
                    </div>
                  </div>

                  <div className="mt-10 grid gap-4 text-left sm:grid-cols-3">
                    <ResultCard icon={<Gauge className="h-5 w-5" />} label="Readiness" value={`${result.score}% · ${result.band}`} />
                    <ResultCard icon={<Compass className="h-5 w-5" />} label="Suggested Model" value={result.entry_model} />
                    <ResultCard icon={<ListChecks className="h-5 w-5" />} label="Next Steps" value={`${result.next_steps.length} recommended actions`} />
                  </div>

                  <div className="mt-8 rounded-2xl border border-white/10 bg-white/[0.02] p-6 text-left">
                    <h3 className="font-head text-lg font-bold text-white">Recommended next steps</h3>
                    <ul className="mt-4 space-y-3">
                      {result.next_steps.map((s, i) => (
                        <li key={i} className="flex items-start gap-3 text-sm text-zinc-300">
                          <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full grad-accent text-[10px] font-bold text-[#1a0f00]">{i + 1}</span>
                          {s}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
                    <button onClick={() => navigate("/contact")} className="btn-primary" data-testid="assessment-result-cta">
                      Book a Free Consultation <ArrowRight className="h-4 w-4" />
                    </button>
                    <button onClick={reset} className="btn-ghost" data-testid="assessment-restart">
                      <RefreshCw className="h-4 w-4" /> Retake
                    </button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>
    </>
  );
}

const Field = ({ label, value, onChange, type = "text", testid }) => (
  <div>
    <label className="mb-2 block text-sm text-zinc-400">{label}</label>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      className="w-full rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 text-sm text-white outline-none transition-colors focus:border-orange-500/50"
    />
  </div>
);

const ResultCard = ({ icon, label, value }) => (
  <div className="rounded-2xl border border-white/8 bg-white/[0.02] p-5">
    <div className="mb-2 text-orange-400">{icon}</div>
    <div className="text-[11px] uppercase tracking-wider text-zinc-500">{label}</div>
    <div className="mt-1 text-sm font-semibold text-white">{value}</div>
  </div>
);
